import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Reveal, Kicker } from "./Reveal";
import { IMAGES, STATS } from "../../data/site";

const Counter = ({ value, suffix, label }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2.2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <div ref={ref} className="border-l border-black/10 pl-5">
      <div
        data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="font-display text-5xl font-bold text-ink md:text-6xl"
      >
        {display}
        <span className="text-bronze">{suffix}</span>
      </div>
      <div className="mt-2 text-xs uppercase tracking-[0.25em] text-fog">
        {label}
      </div>
    </div>
  );
};

export const About = () => (
  <section id="about" data-testid="about-section" className="relative py-24 md:py-36">
    <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2 lg:gap-24">
      <div>
        <Reveal>
          <Kicker index="01" label="About Us" />
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight md:text-6xl">
            Excellence in
            <span className="block text-bronze">Construction</span>
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-8 max-w-xl leading-relaxed text-fog">
            With 19 years of hands-on experience, Sunny's Construction Ltd
            delivers building and structural projects across London and the UK
            — from bespoke residential renovations to complex commercial
            contracts. Our teams combine time-served craftsmanship with
            rigorous structural engineering, so every beam, wall and finish is
            built to last.
          </p>
        </Reveal>
        <Reveal delay={0.24}>
          <p className="mt-5 max-w-xl leading-relaxed text-fog">
            Safety is engineered into everything we do. We operate to the
            Construction (Design &amp; Management) Regulations 2015, maintain
            comprehensive insurance on every contract, and hold ourselves to
            standards that exceed the industry baseline — because trust is
            built the same way as structure: deliberately.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-12 grid grid-cols-3 gap-6 border-t border-black/10 pt-10">
            {STATS.map((stat) => (
              <Counter key={stat.label} {...stat} />
            ))}
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.15} className="relative">
        <div className="relative">
          <div className="absolute inset-0 translate-x-5 translate-y-5 border border-bronze/50" />
          <img
            src={IMAGES.about}
            alt="Two engineers reviewing plans on a Sunny Construction site"
            className="relative h-[420px] w-full object-cover md:h-[560px]"
            loading="lazy"
          />
          <div className="absolute bottom-6 left-6 flex items-center gap-3 bg-white/85 px-5 py-3 backdrop-blur-md">
            <span className="h-2 w-2 bg-safety" />
            <span className="text-xs uppercase tracking-[0.25em] text-ink/80">
              On Site — Hounslow, London
            </span>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
